/**
 * Live mode's earcons on the web (docs/plans/os-voice-conversation.md, The
 * states; step 3.12): each a few soft sine notes, made on the voice player's
 * own `AudioContext` and played beside the voice, never in its queue, so an
 * earcon is never cut by Interrupt nor counted as OS speaking. The phone
 * plays recorded clips (`apps/mobile/assets/sounds`); a browser makes the
 * same shapes from notes, so nothing is fetched.
 *
 * - rising chime: live mode starts listening;
 * - falling chime: live mode ends;
 * - stop tick: a spoken stop parked the work;
 * - two-note prompt: a card needs a click;
 * - Quiet tick: a soft falling tick into Quiet;
 * - soft chime: Still there? and the 30-minute mark;
 * - mute click: Mute.
 */
import type { LiveSound } from '@alluzo/shared';

/** One note: its pitch, when it starts after the earcon's start, how long it sounds, and how loud (0..1). */
export type EarconNote = { readonly hz: number; readonly atMs: number; readonly ms: number; readonly gain: number };

export const EARCONS: Readonly<Record<LiveSound, readonly EarconNote[]>> = {
  chime_rise: [
    { hz: 660, atMs: 0, ms: 140, gain: 0.16 },
    { hz: 880, atMs: 110, ms: 220, gain: 0.16 },
  ],
  chime_fall: [
    { hz: 880, atMs: 0, ms: 140, gain: 0.16 },
    { hz: 587, atMs: 110, ms: 240, gain: 0.16 },
  ],
  stop_tick: [{ hz: 520, atMs: 0, ms: 70, gain: 0.14 }],
  prompt: [
    { hz: 784, atMs: 0, ms: 120, gain: 0.15 },
    { hz: 988, atMs: 160, ms: 160, gain: 0.15 },
  ],
  tick_fall: [
    { hz: 700, atMs: 0, ms: 60, gain: 0.12 },
    { hz: 520, atMs: 60, ms: 90, gain: 0.12 },
  ],
  chime_soft: [{ hz: 740, atMs: 0, ms: 260, gain: 0.1 }],
  mute_click: [{ hz: 1200, atMs: 0, ms: 30, gain: 0.1 }],
};

/** How long an earcon lasts, to its last note's end. */
export const earconMs = (sound: LiveSound): number => Math.max(0, ...EARCONS[sound].map((note) => note.atMs + note.ms));
